import { Fragment } from "react";
import type { ReactElement } from "react";
import { SchalterFeld, WahlFeld, feldId } from "./felder.js";
import { Befunde, restBefunde } from "./Seite.js";
import { mitWert, wertAn } from "../layoutEntwurf.js";
import type { PanelProps } from "../LayoutRegister.js";

const LAGE_TEXT = { nachSummen: "nach den Summen", vorPositionen: "vor den Positionen" } as const;

/** Register „Blöcke": die Schalter des Katalogs. Ein Block zeigt sich nur, wenn der Beleg
 *  auch Inhalt dafür hat — der Schalter kann ihn nur unterdrücken.
 *
 *  Unter dem Schalter „Verbrauchsliste" steht die Lage (`verbrauchsliste.lage`, Vertrag
 *  beleg-layout/1, Entscheid 91 b): Systemwert (nach den Summen) / nach den Summen / vor den
 *  Positionen → Entwurf undefined / nachSummen / vorPositionen. Das Feld steht nur, wenn die
 *  Engine Belegdesign Etappe 2 kennt (`faehig`) — eine ältere lehnte den Schlüssel ab. */
export function BloeckePanel(p: PanelProps & { faehig?: boolean | undefined }): ReactElement {
  const system = p.stand.katalog.verbrauchsliste.lage;
  const roh = wertAn(p.entwurf, ["verbrauchsliste", "lage"]);
  // Ein fremder Wert bleibt sichtbar, der Server meldet ihn.
  const lage = roh === undefined ? "" : typeof roh === "string" ? roh : JSON.stringify(roh);
  const lageOptionen = [
    { wert: "", text: `Systemwert (${LAGE_TEXT[system]})` },
    { wert: "nachSummen", text: LAGE_TEXT.nachSummen },
    { wert: "vorPositionen", text: LAGE_TEXT.vorPositionen },
    ...(lage !== "" && lage !== "nachSummen" && lage !== "vorPositionen"
      ? [{ wert: lage, text: lage }] : []),
  ];
  return (
    <div className="lay__panel">
      <p className="lay__panelhinweis">Ein Block erscheint nur, wenn der Beleg Inhalt dafür
        hat; abgeschaltet bleibt er immer weg.</p>
      {p.stand.katalog.bloecke.map((b) => (
        <Fragment key={b.id}>
          <SchalterFeld id={feldId(`bloecke.${b.id}`)} label={b.label}
            wert={wertAn(p.entwurf, ["bloecke", b.id])} systemwert={b.wert}
            fehler={p.feldFehler[`bloecke.${b.id}`]}
            onWert={(neu) => {
              p.onEntwurf(mitWert(p.entwurf, ["bloecke", b.id], neu));
              p.onFeldVerlassen();
            }} />
          {b.id === "verbraeuche" && p.faehig === true && (
            <>
              <WahlFeld id={feldId("verbrauchsliste.lage")} label="Lage der Verbrauchsliste"
                wert={lage} optionen={lageOptionen}
                fehler={p.feldFehler["verbrauchsliste.lage"]}
                onWahl={(w) => {
                  p.onEntwurf(mitWert(p.entwurf, ["verbrauchsliste", "lage"],
                    w === "" ? undefined : w));
                  p.onFeldVerlassen();
                }} />
              <p className="lay__panelhinweis">„Vor den Positionen“ stellt die Verbrauchsliste mit
                der Überschrift (Register Texte) nach dem Einleitungstext vor die Positionstabelle.
                Die Vorschau zeigt nur Seite 1.</p>
              <Befunde liste={restBefunde(p.feldFehler, "verbrauchsliste",
                (pfad) => pfad === "verbrauchsliste.lage")} />
            </>
          )}
        </Fragment>
      ))}
    </div>
  );
}
